import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import './Login.css';
import PasswordField from './PasswordField';

const REQUEST_STATUS_ALERTS = {
  verified: 'Email verified. A user-admin will now review your request and either approve tenant access or disregard the request.',
  exists: 'An account already exists for this email. Please sign in or use Forgot Password.',
  invalid: 'Your access verification link is invalid or expired. Please request access again.'
};

function Login({ setToken }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmittingLogin, setIsSubmittingLogin] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isSendingForgot, setIsSendingForgot] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestStatus = String(params.get('request') || '').trim().toLowerCase();
    if (!requestStatus) return;

    const alertMessage = REQUEST_STATUS_ALERTS[requestStatus];
    if (alertMessage) {
      alert(alertMessage);
    }

    params.delete('request');
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, '', nextUrl);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoginError('');
    if (isSubmittingLogin) return;

    if (typeof setToken !== 'function') {
      setLoginError('Login handler is not available. Please refresh and try again.');
      return;
    }

    setIsSubmittingLogin(true);
    try {
      const res = await axios.post('/api/auth/login', {
        email: String(email || '').trim(),
        password
      }, {
        timeout: 15000
      });
      
      if (!res.data || !res.data.token) {
        throw new Error('Invalid response from server');
      }

      setToken(res.data.token);
    } catch (err) {
      console.error('Login error:', err);
      const msg = err?.response?.data?.error || err.message || 'Auth failed';
      setLoginError(msg);
    } finally {
      setIsSubmittingLogin(false);
    }
  }

  async function handleForgotPasswordLink(e) {
    e.preventDefault();
    if (isSendingForgot) return;

    const emailToUse = String(email || '').trim() || String(window.prompt('Enter your user ID (email):') || '').trim();
    if (!emailToUse) return;

    setIsSendingForgot(true);
    try {
      await axios.post('/api/auth/forgot-password', { email: emailToUse });
      alert('If that email exists, a reset link has been sent.');
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Failed to request password reset';
      alert(`Error: ${msg}`);
    } finally {
      setIsSendingForgot(false);
    }
  }

  return (
    <div className="login-screen-shell">
      <div className="login-container login-container--stripe">
        <h2>Sign In</h2>
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label>Email</label>
            <input type="email" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Password</label>
            <PasswordField
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password"
              name="loginPassword"
              autoComplete="current-password"
              required
              ariaLabel="password"
            />
          </div>
          <button type="submit" className="login-btn" disabled={isSubmittingLogin}>
            {isSubmittingLogin ? 'Signing in…' : 'Sign In'}
          </button>
          {loginError && <div className="form-error" role="alert">{loginError}</div>}
        </form>

        <div className="login-links" role="group" aria-label="Sign in support links">
          <Link to="/request-access" className="link-btn">Request Access</Link>
          <button type="button" className="link-btn" onClick={handleForgotPasswordLink} disabled={isSendingForgot}>
            {isSendingForgot ? 'Sending reset email…' : 'Forgot Password'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Login;